import FilterFacets from "@/app/features/reports/components/active-filters";
import { ExcludedTestsBadge } from "@/app/features/reports/components/excluded-tests-badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ArrowUpDown } from 'lucide-react'
import React, { SetStateAction, useCallback, useMemo, useState } from 'react'
import { FilterCounts, Filters, FilterTypes, SelectedFilters, FilterLabels, filterNames } from '../types/filters'
import { FilterDrawer } from './filter-drawer'
import { useReportViewContext } from "@/app/features/reports/components/report-viewer-context"
import { HierarchicalSelector } from './space-list-selector'
import { PopupFacetSelector } from "@/app/features/reports/components/popup-facet-selector";
import { FilteredTestCount } from "@/app/features/reports/components/report-filter-bar";


export function FilterButton() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { filters: { filteredCounts, unfilteredCounts, updateFilters, selectedFilters, facets: filterFacetsData }, isEditMode } = useReportViewContext();

  const activeFilters = useMemo(() => {
    return Object.entries(selectedFilters)
      .flatMap(([category, filters]) => filters.map(filter => `${category}:${filter}`)).length > 0
  }, [selectedFilters]);

  return (
    <>
      <Button
        variant={activeFilters ? "default" : "outline"} 
        onClick={() => setIsDrawerOpen(true)}>
        Filter <FilteredTestCount className={`${activeFilters ? "text-white" : "text-zinc-600"}`} />
      </Button>

      <FilterDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        selectedFilters={selectedFilters}
        setSelectedFilters={updateFilters}
        filters={filterFacetsData}
        filterCounts={unfilteredCounts}
        filteredCounts={filteredCounts}
      />

    </>
  )
}

interface ExposedFacetSelectorProps {
  exposedFacet: FilterTypes;
  facets: Filters | undefined;
  selectedFilters: SelectedFilters;
  updateFilters: (updater: SetStateAction<SelectedFilters>) => void;
  unfilteredCounts: FilterCounts | undefined;
  filteredCounts: FilterCounts | undefined;
}

function ExposedFacetSelector({
  exposedFacet,
  facets,
  selectedFilters,
  updateFilters,
  unfilteredCounts,
  filteredCounts
}: ExposedFacetSelectorProps) {
  const handleExposedFacetChange = useCallback((category: FilterTypes, newSelectedIds: string[]) => {
    updateFilters((prevSelectedFilters: SelectedFilters) => {
      return {
        ...prevSelectedFilters,
        [category]: newSelectedIds,
      };
    });
  }, [updateFilters]);

  return <PopupFacetSelector
    name={exposedFacet}
    description={FilterLabels[exposedFacet] || exposedFacet}
    onStateChange={handleExposedFacetChange}
    allFiltersData={facets}
    selectedFilters={selectedFilters}
    filterCounts={unfilteredCounts}
    filteredCounts={filteredCounts}
  />
}


interface FilterSectionProps {
  showSearch?: boolean;
  exposedFacet?: FilterTypes;
  defaultImplicitFilters?: { [key in FilterTypes]?: string[] };
  facets: Filters | undefined;
  selectedFilters: SelectedFilters;
  updateFilters: (updater: SetStateAction<SelectedFilters>) => void;
  removeFilter: (category: FilterTypes, value: string) => void;
  filteredCounts: FilterCounts | undefined;
  unfilteredCounts: FilterCounts | undefined;
}

export function FilterSection({
  showSearch = true,
  exposedFacet,
  defaultImplicitFilters,
  facets,
  selectedFilters,
  updateFilters,
  removeFilter,
  filteredCounts,
  unfilteredCounts
}: FilterSectionProps) {

  return (
    <div className="space-x-2 flex flex-row w-full justify-between">

      <div className="flex items-center space-x-2">
        {exposedFacet && (
          <ExposedFacetSelector
            exposedFacet={exposedFacet}
            facets={facets}
            selectedFilters={selectedFilters}
            updateFilters={updateFilters}
            unfilteredCounts={unfilteredCounts}
            filteredCounts={filteredCounts}
          />
        )}
        <FilterFacets
          exposedFacet={exposedFacet}
          removeFilter={removeFilter}
          selectedFilters={selectedFilters}
          defaultImplicitFilters={defaultImplicitFilters}
        />

      </div>

      <FilterButton />

    </div>
  )
}
